import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, PAYMENT_STATUS_META } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft } from "@phosphor-icons/react";

export default function CustomerDetail() {
  const { id } = useParams();
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setData(r.data));
    api.get(`/customers/${id}/orders`).then((r) => setOrders(r.data));
    if (hasRole("admin", "accounts")) {
      api.get(`/customers/${id}/ledger`).then((r) => setLedger(r.data)).catch(() => {});
    }
  }, [id, hasRole]);

  if (!data) return <div className="label-mini">Loading…</div>;

  const waUrl = data.whatsapp
    ? `https://wa.me/${data.whatsapp.replace(/\D/g, "")}`
    : null;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <Link to="/customers" className="text-xs label-mini flex items-center gap-1 hover:text-black">
        <ArrowLeft size={12} /> Back to Customers
      </Link>
      <div className="card-sharp p-6 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight">{data.name}</h1>
            <span className="status-badge" style={{ background: data.type === "B2B" ? "#E0E7FF" : "#F4F4F5", color: data.type === "B2B" ? "#0020C2" : "#52525B", borderColor: data.type === "B2B" ? "#0020C2" : "#E4E4E7" }}>{data.type}</span>
            <span className="font-mono text-xs text-zinc-500">{data.code}</span>
          </div>
          {data.company && <div className="text-zinc-600 mt-1">{data.company}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5">
            <Info label="Phone" value={data.phone} />
            <Info label="Email" value={data.email} />
            <Info label="WhatsApp" value={data.whatsapp} link={waUrl} />
            <Info label="City" value={data.city} />
            <Info label="Country" value={data.country} />
            <Info label="Currency" value={data.currency_pref} />
          </div>
          {data.address && <Info label="Address" value={data.address} block />}
        </div>
        <div className="card-sharp p-4 bg-zinc-50 border-zinc-200">
          <div className="label-mini">Outstanding Balance</div>
          <div className="kpi-num text-3xl mt-2 tnum" data-testid="customer-balance">{fmtMoney(data.balance || 0)}</div>
          <div className="text-[11px] text-zinc-500 mt-1">PKR equivalent at booking rate</div>
        </div>
      </div>

      <div className="card-sharp p-5">
        <h3 className="text-lg font-semibold tracking-tight mb-3">Order History</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left label-mini border-b border-zinc-200">
                <th className="py-2">Order</th>
                <th className="py-2">Date</th>
                <th className="py-2">Status</th>
                <th className="py-2 text-right">Total (PKR)</th>
                <th className="py-2">Payment</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-zinc-100">
                  <td className="py-2 font-mono text-xs">
                    <Link to={`/orders/${o.id}`} className="underline font-semibold">{o.code}</Link>
                  </td>
                  <td className="py-2 text-xs">{fmtDate(o.created_at)}</td>
                  <td className="py-2"><StatusBadge status={o.status} /></td>
                  <td className="py-2 text-right font-mono tnum">{fmtMoney(o.total_sell || 0, "")}</td>
                  <td className="py-2 text-xs">
                    {(() => {
                      const ps = PAYMENT_STATUS_META[o.payment_status] || PAYMENT_STATUS_META.unpaid;
                      return (
                        <span className="status-badge" style={{ background: ps.bg, color: ps.fg, borderColor: ps.bd }}>
                          {ps.label}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-xs text-zinc-500">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {ledger && (
        <div className="card-sharp p-5">
          <h3 className="text-lg font-semibold tracking-tight mb-3">Ledger</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left label-mini border-b border-zinc-200">
                  <th className="py-2">Date</th>
                  <th className="py-2">Description</th>
                  <th className="py-2 text-right">Debit</th>
                  <th className="py-2 text-right">Credit</th>
                  <th className="py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.transactions.map((t) => (
                  <tr key={t.id} className="border-b border-zinc-100">
                    <td className="py-2 text-xs font-mono">{fmtDate(t.date)}</td>
                    <td className="py-2 text-xs">{t.description}</td>
                    <td className="py-2 text-right font-mono tnum">{t.txn_type === "debit" ? fmtMoney(t.amount, "") : "—"}</td>
                    <td className="py-2 text-right font-mono tnum">{t.txn_type === "credit" ? fmtMoney(t.amount, "") : "—"}</td>
                    <td className="py-2 text-right font-mono tnum font-semibold">{fmtMoney(t.balance_after, "")}</td>
                  </tr>
                ))}
                {ledger.transactions.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-xs text-zinc-500">No ledger entries.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ label, value, link, block }) => (
  <div className={block ? "col-span-full mt-3" : ""}>
    <div className="label-mini">{label}</div>
    {link ? (
      <a href={link} target="_blank" rel="noreferrer" className="text-sm font-medium underline">{value || "—"}</a>
    ) : (
      <div className="text-sm font-medium">{value || "—"}</div>
    )}
  </div>
);
